import { supabase } from '../supabase'
import type { DbEnum } from '../types/db-rows'

export type CoverageEligibility = {
  vehicleCategory: string
  eligibleProviders: string[]
  ownPolicyAllowed: boolean
  platformExemptionAllowed: boolean
}

type VehicleCategory = DbEnum<'vehicle_category'>

const DEFAULT_ELIGIBILITY = (vehicleCategory: string): CoverageEligibility => ({
  vehicleCategory,
  eligibleProviders: [],
  ownPolicyAllowed: true,
  platformExemptionAllowed: false,
})

class CoverageEligibilityService {
  async forCategory(
    vehicleCategory: VehicleCategory,
    vehicleYear: number | null,
  ): Promise<CoverageEligibility> {
    const fallback = DEFAULT_ELIGIBILITY(vehicleCategory)
    const { data, error } = await supabase.rpc('coverage_eligibility_for_vehicle', {
      p_category: vehicleCategory,
      p_year: vehicleYear,
    })

    // This is a UI hint only. The database and edge functions remain
    // authoritative, so a stale client keeps the existing own-policy flow.
    if (error || !data || typeof data !== 'object' || Array.isArray(data)) return fallback
    const rule = data as {
      vehicle_category: string
      eligible_providers: string[]
      own_policy_allowed: boolean
      platform_exemption_allowed: boolean
    }

    return {
      vehicleCategory: rule.vehicle_category,
      eligibleProviders: rule.eligible_providers.map((provider) => provider.toLowerCase()),
      ownPolicyAllowed: rule.own_policy_allowed,
      platformExemptionAllowed: rule.platform_exemption_allowed,
    }
  }

  async isQualitasEligible(
    vehicleCategory: VehicleCategory,
    vehicleYear: number | null,
  ): Promise<boolean> {
    const eligibility = await this.forCategory(vehicleCategory, vehicleYear)
    return eligibility.eligibleProviders.includes('qualitas')
  }
}

export const coverageEligibilityService = new CoverageEligibilityService()
